import { Dispatch } from "@reduxjs/toolkit";
import { UIAvailableExtraProducts, UIFlightData, UIPreOptInProductType, UIPreOptInSelected } from "@flights/types";
import { PRE_OPTED_ANCILLARIES_QUERY_PARAMS } from "../constants";
import { actions as extraProductActions } from "../ancillaries/store/extraProducts/actions";

export const preOptedAncillariesFromScreenQueryToStore = (flight: UIFlightData, dispatch: Dispatch) => {
  const screenQuery = new URLSearchParams(location.search);

  /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
  /* TODO(asamilyak): simplify this mapping and logic below once we fully migrate to Checkout 2.0 and get rid of
          pre opted ancillaries during checkout process (i.e. mobileTravelPlan and checkoutFlexibleTicket) =>
          by then we won't need one-ancillary to many-query-params mapping (for flexible ticket)
  */
  const ancillaryToQueryParamName: {
    [key in UIPreOptInProductType]: typeof PRE_OPTED_ANCILLARIES_QUERY_PARAMS[keyof typeof PRE_OPTED_ANCILLARIES_QUERY_PARAMS][];
  } = {
    mobileTravelPlan: [PRE_OPTED_ANCILLARIES_QUERY_PARAMS.mobileTravelPlan],
    flexibleTicket: [
      PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket,
      PRE_OPTED_ANCILLARIES_QUERY_PARAMS.checkoutFlexibleTicket
    ],
    checkedInBaggage: [PRE_OPTED_ANCILLARIES_QUERY_PARAMS.checkedInBaggage],
    cancelForAnyReason: [PRE_OPTED_ANCILLARIES_QUERY_PARAMS.cancelForAnyReason]
  };

  Object.keys(ancillaryToQueryParamName).forEach((ancillary: UIPreOptInProductType) => {
    const paramNames = ancillaryToQueryParamName[ancillary];

    if (paramNames.some((paramName) => screenQuery.has(paramName))) {
      const isAvailable = flight.ancillaries && flight.ancillaries[ancillary];
      if (isAvailable) {
        dispatch(extraProductActions.addPreOptInProduct(ancillary, true));
      }
    } else {
      dispatch(extraProductActions.removePreOptInProduct(ancillary));
    }
  });
};

export const metaPreOptedAncillariesFromCheckoutScreenQueryToApiQuery = (): URLSearchParams => {
  const screenQuery = new URLSearchParams(location.search);
  const apiQuery = new URLSearchParams();

  const products: Array<string> = [];
  if (screenQuery.get(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket)) {
    products.push("EXTRA.AIR_REBOOKING");
  }
  if (screenQuery.get(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.checkedInBaggage)) {
    products.push("EXTRA.BAGGAGE");
  }

  if (products.length > 0) {
    apiQuery.set("selectedProducts", products.join(","));
  }

  return apiQuery;
};

export const metaPreOptedAncillariesFromFlightDetailsScreenQueryToApiQuery = (
  screenQuery: URLSearchParams
): URLSearchParams => {
  const screenQueryParam = screenQuery.get("selectedProducts");
  const apiQuery = new URLSearchParams();

  if (screenQueryParam) {
    apiQuery.set("selectedProducts", screenQueryParam);
  }

  return apiQuery;
};

export const metaPreOptedAncillariesFromFlightToStore = (
  ancillaries: UIAvailableExtraProducts,
  actions: typeof extraProductActions
) => {
  /**
   * Reset preOptIn before setting.
   * This is needed in case the user goes back to search results after landing from meta and chooses a new flight.
   * We should not preserve the ancillary selection from meta in this case.
   */
  actions.resetPreOptIn();

  const screenQuery = new URLSearchParams(location.search);
  const { flexibleTicket, checkedInBaggage } = ancillaries;
  if (flexibleTicket?.preSelected || screenQuery.get(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.flexibleTicket)) {
    actions.addPreOptInProduct("flexibleTicket", true);
  }
  if (screenQuery.get(PRE_OPTED_ANCILLARIES_QUERY_PARAMS.cancelForAnyReason)) {
    actions.addPreOptInProduct("cancelForAnyReason", true);
  }

  const isCheckedInBaggageAvailableAndPreSelected = checkedInBaggage?.options;
  if (isCheckedInBaggageAvailableAndPreSelected) {
    isCheckedInBaggageAvailableAndPreSelected.forEach((option) => {
      option.preSelected && actions.addPreOptInProduct("checkedInBaggage", true);
    });
  }
};

export const preOptedAncillariesURLParams = (preOptedAncillaries: UIPreOptInSelected): URLSearchParams => {
  const preOptedParams = new URLSearchParams();

  for (const ancillary in preOptedAncillaries) {
    preOptedParams.set(ancillary, "true");
  }

  return preOptedParams;
};
